/**
 * Qué dice el chip del reloj de respuesta (`components/SlaChip.tsx`).
 *
 * POR QUÉ EXISTE (DEF-001, 01-oct-2026)
 * -------------------------------------
 * Regla de negocio del 01-oct: si una persona ya le escribió al cliente, la
 * conversación NO se reasigna al vencer el plazo; se queda con ese asesor
 * hasta que un supervisor la mueva (`conversation/_handle_asesor_sla`). El
 * tooltip seguía diciendo «Se reasigna automáticamente a otro asesor»: le
 * prometía al supervisor algo que el bot ya no hace.
 *
 * LO QUE CAMBIÓ EL 20-OCT-2026 (documento de QA «Asignación de conversaciones»)
 * --------------------------------------------------------------------------
 * - El plazo del primer mensaje es de 10 minutos HÁBILES y fuera del horario
 *   de atención el reloj está en pausa (`paused`, lo resuelve el backend).
 * - Al vencer, pasa a otro asesor que no haya dejado vencer el plazo ese día;
 *   si no queda ninguno, o ya pasó por tres, queda «para revisar» y no rota
 *   más: el chip no puede prometer otra reasignación.
 *
 * `atendida` y `paraRevisar` los resuelve el backend (`_resumen_conversacion`)
 * con las MISMAS condiciones que el reloj, para que el chip no las deduzca.
 *
 * Funciones puras y sin imports a propósito: el test las transpila con el
 * TypeScript del portal y las corre en Node
 * (`tests/unit/test_qa_test_01oct_portal_pendientes.py`).
 */

/**
 * «CLIENTE ESPERANDO»: UNA definición para todas las pantallas (DEF-112, QA de
 * TEST 22-oct-2026). El mismo minuto En vivo decía «2 clientes», Asesores
 * «acasallo: 1», el Panel listaba dos filas esperando a acasallo y tres líneas
 * más abajo decía que tenía uno, y su KPI decía 3. Eran cinco criterios. Hoy
 * la regla vive en el backend (`negocio.espera_del_cliente`) y llega por fila
 * como `esperando_min`; acá solo se cuenta y se dice qué se cuenta.
 *
 * Los plazos son los de `shared/sla_asesor.py` (`SLA_LIMITE_MIN`,
 * `PLAZO_SIGUIENTES_MIN`), cruzados por test.
 */
export const PLAZO_PRIMER_MENSAJE_MIN = 10
export const PLAZO_SIGUIENTES_MIN = 30

/** Lo que cada pantalla dice que cuenta, con las mismas palabras. */
export const QUE_ES_UN_CLIENTE_ESPERANDO =
  `Cliente esperando: lo atiende una persona y lleva más de ${PLAZO_PRIMER_MENSAJE_MIN} min `
  + `hábiles sin respuesta (${PLAZO_SIGUIENTES_MIN} si su asesor ya le escribió). Fuera del `
  + 'horario de atención el reloj se detiene.'

/** ¿Este cliente cuenta como esperando fuera de plazo? */
export function esperandoFueraDePlazo(c: { esperando_min?: number | null }): boolean {
  return typeof c.esperando_min === 'number'
}

/** Lo que el chip necesita del reloj; es un subconjunto de `AdvisorSla`. */
export interface RelojDelChip {
  level: string
  minutes_left: number
  reminders_sent: number
  /** Fuera del horario de atención: el reloj no corre. */
  paused?: boolean
}

/** El `title` del chip: qué pasa cuando se cumple el plazo. */
export function tituloDelSla(sla: RelojDelChip, atendida: boolean,
                             paraRevisar = false): string {
  const avisos = sla.reminders_sent ? ` · ${sla.reminders_sent} aviso(s) enviados` : ''
  const pausa = sla.paused ? ' · en pausa: fuera del horario de atención' : ''
  if (atendida) {
    return (sla.level === 'breached'
      ? 'Venció el plazo de respuesta. La atiende su asesor: queda con él hasta que un supervisor la mueva'
      : `Quedan ${sla.minutes_left} min del plazo de respuesta. La atiende su asesor` + avisos) + pausa
  }
  if (paraRevisar) {
    return 'Para revisar: ningún asesor le respondió. No se reasigna más: queda con su asesor hasta que un supervisor la mueva' + pausa
  }
  return (sla.level === 'breached'
    ? 'Se reasigna a otro asesor que no haya dejado vencer el plazo hoy; si no queda ninguno, queda para revisar'
    : `Quedan ${sla.minutes_left} min antes de que se reasigne` + avisos) + pausa
}
